import { Component, ElementRef, OnInit, Renderer2, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Constants, SearchOperations } from 'src/app/app.constants';
import { AlertService } from 'src/app/core/alert.service';
import { AuthService } from 'src/app/core/auth.service';
import { Dialog, DialogService } from 'src/app/core/dialog.service';
import { LoadingService } from 'src/app/core/loading.service';
import { TranslatorService } from 'src/app/core/translator.service';
import { UtilityService } from 'src/app/core/utility.service';
import { BaseFormComponent } from 'src/app/shared/base/base-form.component';
import { BaseService } from 'src/app/shared/base/base.service';
import { ValueLabel } from 'src/app/shared/base/base.models';
import { BusinessPartnerStatus, BusinessPartnerType, BusinessPartnerGroup, BusinessPartner, DeliveryLocation, DeliveryLocationType, DeliveryLocationStatus } from '../business-partners.models';
import { BusinessPartnersService } from '../business-partners.service';
import { OptionsSource } from 'src/app/core/core.models';
import { CountriesService } from '../../countries/countries.service';
import { DxDataGridComponent, DxFormComponent } from 'devextreme-angular';
import { TranslateService } from '@ngx-translate/core';
import { Country, CountryConfigurationKey } from '../../countries/countries.models';

@Component({
  selector: 'app-business-partners-form',
  templateUrl: './business-partners-form.component.html',
  styleUrls: ['./business-partners-form.component.scss']
})
export class BusinessPartnersFormComponent extends BaseFormComponent implements OnInit {
  @ViewChild('deliveryLocationsGrid') deliveryLocationsGrid: DxDataGridComponent;
  @ViewChild('popupForm') popupForm: DxFormComponent;

  public statuses: ValueLabel[] = [];
  public types: ValueLabel[] = [];
  public groups: ValueLabel[] = [];
  public deliveryTypes: ValueLabel[] = [];
  public deliveryStatuses: ValueLabel[] = [];
  public countries: OptionsSource;
  public idNumberLength: number = 99;
  public vatNumberLength: number = 99;
  public numberPattern = "^[0-9]*$";
  public tabs: string[] = ['DELIVERY_LOCATIONS'];
  public deliveryLocations: DeliveryLocation[] = [];
  public addDeliveryLocationModalVisible: boolean = false;
  public deliveryLocation: DeliveryLocation = new DeliveryLocation();
  public isStatusModalVisible: boolean = false;
  public selectedDeliveryLocation: DeliveryLocation = new DeliveryLocation();
  public deliveryIdNumberLength: number = 99;
  public reportUrlForLocations = `${Constants.contextPath}/${this.businessPartnerService.urlDeliveryLocation}`;
  public filterDeliveryLocations = `${SearchOperations.KEY_SEARCH}=${SearchOperations.LEFT_PARENTHESES_READABLE}businessPartner${SearchOperations.SPLIT_JOIN_OPERATOR}id${SearchOperations.EQUALITY}${this.id}${SearchOperations.RIGHT_PARENTHESES_READABLE}`;
  private CountryConfigurationKey = CountryConfigurationKey;

  constructor(
    private countriesService: CountriesService,
    public authService: AuthService,
    public loadingService: LoadingService,
    public translatorService: TranslatorService,
    public utilityService: UtilityService,
    public translateService: TranslateService,
    protected activatedRoute: ActivatedRoute,
    protected baseService: BaseService,
    protected dialogService: DialogService,
    protected router: Router,
    protected alertService: AlertService,
    private businessPartnerService: BusinessPartnersService
  ) {
    super(authService, loadingService, translatorService, utilityService, activatedRoute, baseService, dialogService, router, alertService)
  }

  ngOnInit(): void {
    this.authorities.init('C_PRTNR', 'R_PRTNR', 'U_PRTNR', 'D_PRTNR');
    this.url = `${Constants.contextPath}/partners`;
    this.prepareLookupData();
    this.prepareFormData(new BusinessPartner(), (response: BusinessPartner) => {
      return this.modifyResponse(response);
    });
  }

  prepareLookupData() {
    this.countries = this.countriesService.getCountriesOptions();
    this.statuses = this.utilityService.prepareLookupItems(this.utilityService.enumToSelectArray(BusinessPartnerStatus, true), 'value', 'label', 'ENUMS.BUSINESS_PARTNER_STATUS.');
    this.types = this.utilityService.prepareLookupItems(this.utilityService.enumToSelectArray(BusinessPartnerType, true), 'value', 'label', 'ENUMS.BUSINESS_PARTNER_TYPES.');
    this.groups = this.utilityService.prepareLookupItems(this.utilityService.enumToSelectArray(BusinessPartnerGroup, true), 'value', 'label', 'ENUMS.BUSINESS_PARTNER_TYPES.');
    this.deliveryTypes = this.utilityService.prepareLookupItems(this.utilityService.enumToSelectArray(DeliveryLocationType, true), 'value', 'label', 'ENUMS.DELIVERY_LOCATION_TYPES.');
    this.deliveryStatuses = this.utilityService.prepareLookupItems(this.utilityService.enumToSelectArray(DeliveryLocationStatus, true), 'value', 'label', 'ENUMS.DELIVERY_LOCATION_STATUS.');
  }

  public countryChanged = (event) => {
    if (typeof event.value !== 'string' || (event.previousValue && event.value == event.previousValue)) return
    this.getCompanyConfiguration(event.value);
  }

  public deliveryPlaceCountryChanged = (event) => {
    if (typeof event.value !== 'string' || (event.previousValue && event.value == event.previousValue)) return
    this.getCompanyConfiguration(event.value, false);
  }

  //countryForBusinessPartner is default for main form, else is for deliveryPlace country
  private getCompanyConfiguration(companyId: string, countryForBusinessPartner: boolean = true) {
    if (!companyId) return;
    // if deliveryLocation country is same as buissiness partner    
    if (!countryForBusinessPartner && companyId == this.formData.country.id) {
      this.deliveryIdNumberLength = this.idNumberLength;
      this.restartDataIfNeeded();
      return
    }
    this.countriesService.getCountryConfiguration(companyId, (res) => {

      let vatNumberForCompany = res.find(c => c.type == CountryConfigurationKey[CountryConfigurationKey.VAT_NUMBER_LENGTH]);
      if (vatNumberForCompany)
        this.vatNumberLength = parseInt(vatNumberForCompany.value);

      let idNumberForCompany = res.find(c => c.type == CountryConfigurationKey[CountryConfigurationKey.ID_NUMBER_LENGTH]);
      if (idNumberForCompany) {
        if (countryForBusinessPartner)
          this.idNumberLength = parseInt(idNumberForCompany.value);
        else // country for delivery location
          this.deliveryIdNumberLength = parseInt(idNumberForCompany.value);
      }

      this.restartDataIfNeeded();
    });
  }

  restartDataIfNeeded() {
    if (this.formData?.idNumber && (this.formData?.idNumber.length > this.idNumberLength)) this.formData.idNumber = null;
    if (this.formData?.vatNumber && (this.formData?.vatNumber.length > this.vatNumberLength)) this.formData.vatNumber = null;
  }

  modifyResponse(response: BusinessPartner) {
    this.getCompanyConfiguration(response.country.id);
    if(this.formData?.country)
      this.formData.country = (this.formData.country)?.id;

    if (response.id) {
      this.businessPartnerService.getAllDeliveryLocationsByBusinessPartner(response.id, this.filterDeliveryLocations, (res) => {
        this.deliveryLocations = res;
      });
    }

    return response;
  }

  changeBusinessPartnerStatus(value) {
    if (value == this.formData.status) return;
    let dialogData = new Dialog(`BUSINESS_PARTNER.CHANGE_STATUS.${value}`, () => { // translations is based on partner status
      this.businessPartnerService.changeBusinessPartnerStatus(this.url, value, <string>this.id, (res: BusinessPartner) => {
        this.formData = res;
        this.alertService.success('SHARED.SAVE_SUCCESS');
        setTimeout(() => {
          this.pendingChanges = false;
        }, 100);
      })
    }, () => {
    });
    this.dialogService.show(dialogData);
  }

  public onToolbarPreparingAttributes($event: any): void {
    //remove default add button
    $event.toolbarOptions.items.pop();

    if (this.authorities.update) {
      $event.toolbarOptions.items.unshift({
        location: 'after',
        widget: 'dxButton',
        options: {
          icon: 'plus',
          hint: this.translateService.instant('DELIVERY_LOCATIONS.ADD'),
          text: this.translateService.instant('DELIVERY_LOCATIONS.ADD'),
          showText: 'inMenu',
          onClick: this.addDeliveryLocation.bind(this)
        },
        locateInMenu: 'auto'
      });
    }
  }

  public editingDeliveryLocation($event: any): void {
    $event.cancel = true;
    this.deliveryLocation = $event.data;
    this.addDeliveryLocationModalVisible = true;
    this.getCompanyConfiguration($event.data.country?.id, false);
  }

  public deletedDeliveryLocation($event: any): void {
    this.businessPartnerService.deleteDeliveryLocation($event.data.id, (res) => {
      this.alertService.success('SHARED.DELETE_SUCCESS');
      this.pendingChanges = false;
    });
  }

  public addDeliveryLocation() {
    this.addDeliveryLocationModalVisible = true;
    this.deliveryLocation = new DeliveryLocation();
  }

  public closeAddDeliveryModal() {
    this.addDeliveryLocationModalVisible = false;
  }

  public saveDeliveryLocation() {
    if (this.popupForm.instance.validate() != null && !this.popupForm.instance.validate().isValid) {
      this.alertService.error('SHARED.INVALID_FORM');
      return;
    }

    this.deliveryLocation.businessPartner = this.formData.id;
    this.businessPartnerService.createDeliveryLocation(this.deliveryLocation, (res) => {
      this.addDeliveryLocationModalVisible = false;
      this.alertService.success('SHARED.SAVE_SUCCESS');
      this.pendingChanges = false;
      this.deliveryLocations.push(res)
    });
  }

  public updateDeliveryLocation() {
    if (this.popupForm.instance.validate() != null && !this.popupForm.instance.validate().isValid) {
      this.alertService.error('SHARED.INVALID_FORM');
      return;
    }
    let data = { ...this.deliveryLocation };
    data.businessPartner = this.formData.id;
    data.country = (<Country>this.deliveryLocation.country).id || this.deliveryLocation.country;
    this.businessPartnerService.updateDeliveryLocation(data, (res) => {
      this.addDeliveryLocationModalVisible = false;
      this.alertService.success('SHARED.SAVE_SUCCESS');
      this.pendingChanges = false;
      this.deliveryLocations[this.deliveryLocations.findIndex(x => x.id == data.id)].version = res.version;
    });
  }

  public changeDelievryStatus(data: any) {
    this.selectedDeliveryLocation = data;
    this.isStatusModalVisible = true;
  }

  public closeStatusModal() {
    this.isStatusModalVisible = false;
  }

  public saveStatusDelivery() {
    switch (this.selectedDeliveryLocation.status.toString()) {
      case 'ACTIVE':
        this.businessPartnerService.activateDeliveryLocation(this.selectedDeliveryLocation.id, (res) => {
          this.deliveryLocations[this.deliveryLocations.findIndex(x => x.id == res.id)].status = res.status;
          this.deliveryLocations[this.deliveryLocations.findIndex(x => x.id == res.id)].version = res.version;
          this.alertService.success('SHARED.SAVE_SUCCESS');
        })
        break;
      case 'INACTIVE':
        this.businessPartnerService.deactivateDeliveryLocation(this.selectedDeliveryLocation.id, (res) => {
          this.deliveryLocations[this.deliveryLocations.findIndex(x => x.id == res.id)].status = res.status;
          this.deliveryLocations[this.deliveryLocations.findIndex(x => x.id == res.id)].version = res.version;
          this.alertService.success('SHARED.SAVE_SUCCESS');
        })
        break;
      case 'SUSPENDED':
        this.businessPartnerService.suspendDeliveryLocation(this.selectedDeliveryLocation.id, (res) => {
          this.deliveryLocations[this.deliveryLocations.findIndex(x => x.id == res.id)].status = res.status;
          this.deliveryLocations[this.deliveryLocations.findIndex(x => x.id == res.id)].version = res.version;
          this.alertService.success('SHARED.SAVE_SUCCESS');
        })
        break;
    }

    this.isStatusModalVisible = false;
  }

  public saveChanges(): void {
    if (!this.isFormValid())
      return;

    let data = this.modifyBeforeUpdate(this.formData);

    if (this.id == null)
      this.baseService.create(this.url, data, () => {
        this.onSaveSuccess();
      });
    else
      this.baseService.update(this.url, data, () => {
        this.onSaveSuccess();
      });
  }

  modifyBeforeUpdate(formData) {
    if (this.formData.country != null && this.formData.country.id != null)
      this.formData.country = this.formData.country.id;

    return formData;
  }
}
